// src/context/PlaceContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  savePlace,
  fetchPairings,
  createPairing,
  getUserVotes,
  getUserFavorites,
} from '../services/api';
import { useAuth } from './AuthContext';

const PlaceContext = createContext();

export const PlaceProvider = ({ children }) => {
  const { user, role, getToken } = useAuth();
  const userId = user?.id || null;

  const [primaryPlace, setPrimaryPlaceState] = useState(null);
  const [pairings, setPairings] = useState([]);
  const [loadingPairings, setLoadingPairings] = useState(false);

  // userVotes[pairing_id] = { voteType, weight }
  const [userVotes, setUserVotes] = useState({});

  // The user's saved (favorited) places
  const [savedPlaces, setSavedPlaces] = useState([]);

  // Track which metro we are filtering by
  const [metroFilter, setMetroFilter] = useState('All');
  
  // Track if this is an initial place load for auto-switch logic
  const [isInitialPlaceLoad, setIsInitialPlaceLoad] = useState(true);

  // Track which place is currently "selected" for auto-pairing
  const [selectedPlaceInternalId, setSelectedPlaceInternalId] = useState(null);
  // The second place that triggers a pairing once it's set
  const [secondPlaceInternalId, setSecondPlaceInternalId] = useState(null);

  /**
   * syncPairingsAndVotes(internalId, optionalOverrideMetro)
   */
  const syncPairingsAndVotes = async (internalId, overrideMetro) => {
    if (!internalId) return;
    try {
      const finalMetro = overrideMetro !== undefined ? overrideMetro : metroFilter;

      const token = getToken();
      // First get pairings with the selected metro filter
      const pairingsData = await fetchPairings(internalId, finalMetro);
      
      // Only do auto-switching on initial place load
      if (pairingsData.length === 0 && finalMetro !== 'All' && isInitialPlaceLoad) {
        console.log(`No pairings found for metro "${finalMetro}" on initial load. Checking "All"...`);
        const allPairings = await fetchPairings(internalId, 'All');
        
        // Only auto-switch if "All" actually has results
        if (allPairings.length > 0) {
          setMetroFilter('All');
          setPairings(allPairings || []);
        } else {
          // If "All" also has no results, keep the current filter but with empty pairings
          setPairings([]);
        }
      } else {
        // Normal flow - use the results we got
        setPairings(pairingsData || []);
      }
      
      // After the initial load, mark future calls as non-initial
      setIsInitialPlaceLoad(false);

      const votesData = token ? await getUserVotes(token) : [];

      const votesMap = {};
      (votesData || []).forEach((v) => {
        votesMap[v.pairing_id] = {
          voteType: v.vote_type,
          weight: v.weight || 1,
        };
      });
      setUserVotes(votesMap);
    } catch (err) {
      console.error('[PlaceContext] syncPairingsAndVotes error:', err.message);
    }
  };

  /**
   * setPrimaryPlaceManually
   */
  const setPrimaryPlaceManually = (placeObj) => {
    setPrimaryPlaceState(placeObj);
    setSelectedPlaceInternalId(placeObj.internalId);
    setIsInitialPlaceLoad(true); // Mark as initial load

    if (placeObj?.metro_area) {
      setMetroFilter(placeObj.metro_area);
      syncPairingsAndVotes(placeObj.internalId, placeObj.metro_area);
    } else {
      setMetroFilter('All');
      syncPairingsAndVotes(placeObj.internalId, 'All');
    }
  };

  /**
   * Clear out the current primary place & pairings
   */
  const clearPrimaryPlace = () => {
    setPrimaryPlaceState(null);
    setPairings([]);
    setLoadingPairings(false);
    setUserVotes({});
    setMetroFilter('All');
    setSelectedPlaceInternalId(null);
    setSecondPlaceInternalId(null);
    setIsInitialPlaceLoad(true); // Reset the initial load flag
  };

  /**
   * loadUserFavorites
   */
  const loadUserFavorites = async () => {
    if (!userId) return;
    try {
      const token = getToken();
      console.log('[DEBUG] loadUserFavorites => token:', token);

      const data = await getUserFavorites(token);
      setSavedPlaces(data);
    } catch (err) {
      console.error('[PlaceContext] loadUserFavorites error:', err.message);
    }
  };

  const loadUserVotes = async () => {
    if (!userId) return;
    try {
      const token = getToken();
      const votes = await getUserVotes(token);
      const votesMap = {};
      (votes || []).forEach((v) => {
        votesMap[v.pairing_id] = {
          voteType: v.vote_type,
          weight: v.weight || 1,
        };
      });
      setUserVotes(votesMap);
    } catch (err) {
      console.error('[PlaceContext] loadUserVotes error:', err.message);
    }
  };

  const loadPairings = async (internalId) => {
    setLoadingPairings(true);
    try {
      const data = await fetchPairings(internalId);
      setPairings(data || []);
    } catch (err) {
      console.error('[PlaceContext] loadPairings error:', err.message);
      setPairings([]);
    } finally {
      setLoadingPairings(false);
    }
  };

  /**
   * handlePlaceSelected
   * - user picks a place from the search
   * - if isPrimary=true, we clear & set that as main place
   * - else, we set it as the "second place" -> create pairing
   * - we also auto-switch metroFilter to the new place's metro if it's not primary
   */
  const handlePlaceSelected = async (placeDetails, isPrimary) => {
    try {
      if (isPrimary) {
        clearPrimaryPlace();
      }
      // Save the new place to DB
      const data = await savePlace(placeDetails);
      const updated = {
        ...placeDetails,
        internalId: data.id,
        slug: data.slug,
        metro_area: data.metro_area || null,
      };

      if (isPrimary) {
        // Don't set it as primary in state yet - just return the data so
        // the component can navigate directly to the slug page
        return updated;
      } else {
        // set second place => triggers pairing creation in the useEffect below
        setSecondPlaceInternalId(data.id);

        // *** NEW logic: auto-switch to the new place's metro
        if (updated.metro_area) {
          setMetroFilter(updated.metro_area);
        } else {
          setMetroFilter('All');
        }
      }
      return updated;
    } catch (err) {
      console.error('[PlaceContext] handlePlaceSelected error:', err.message);
      throw err;
    }
  };

  /**
   * handleCreatePairing
   */
  const handleCreatePairing = async (place1InternalId, place2InternalId) => {
    if (!userId) {
      alert('Please log in to create pairings');
      return;
    }
    try {
      const token = getToken();
      await createPairing(token, place1InternalId, place2InternalId);

      // Re-sync after creation
      await syncPairingsAndVotes(place1InternalId);
    } catch (err) {
      console.error('[PlaceContext] handleCreatePairing error:', err.message);
    }
  };

  // Whenever we have both a selected & second => create pairing
  useEffect(() => {
    if (selectedPlaceInternalId && secondPlaceInternalId) {
      handleCreatePairing(selectedPlaceInternalId, secondPlaceInternalId);
      setSecondPlaceInternalId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaceInternalId, secondPlaceInternalId]);

  // If user logs in/out, load favorites
  useEffect(() => {
    if (userId) {
      loadUserFavorites();
    } else {
      setSavedPlaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // If userId/role changes after we have a primaryPlace, re-sync
  useEffect(() => {
    if (primaryPlace?.internalId && userId) {
      syncPairingsAndVotes(primaryPlace.internalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, role, primaryPlace]);

  return (
    <PlaceContext.Provider
      value={{
        primaryPlace,
        pairings,
        loadingPairings,
        userVotes,
        savedPlaces,
        metroFilter,
        setMetroFilter,

        setPrimaryPlace: setPrimaryPlaceManually,
        clearPrimaryPlace,
        handlePlaceSelected,
        loadPairings,
        loadUserVotes,
        loadUserFavorites,
        syncPairingsAndVotes,
      }}
    >
      {children}
    </PlaceContext.Provider>
  );
};

export const usePlaces = () => useContext(PlaceContext);